import { ResolvedTrackingLaneNode, TrackingLaneNode, TrackingLaneNodeStatus } from '../../model/iTrackingLane';

const DAY_MS = 24 * 60 * 60 * 1000;

export function resolveTrackingLaneNodes(
  nodes: TrackingLaneNode[],
  currentNodeId: string | null
): ResolvedTrackingLaneNode[] {
  const resolved = resolveStatuses(nodes, currentNodeId);
  simulateDates(resolved, indexById(nodes));
  return resolved;
}

function resolveStatuses(
  nodes: TrackingLaneNode[],
  currentNodeId: string | null
): ResolvedTrackingLaneNode[] {
  const pathIds = currentNodeId ? findAncestorPath(nodes, currentNodeId) : null;
  if (!pathIds) {
    return forceStatus(nodes, 'upcoming');
  }

  const resolveLevel = (list: TrackingLaneNode[]): ResolvedTrackingLaneNode[] => {
    let cursorPassed = false;

    return list.map(node => {
      let status: TrackingLaneNodeStatus;
      if (cursorPassed) {
        status = 'upcoming';
      } else if (pathIds.has(node.id)) {
        status = 'current';
        cursorPassed = true;
      } else {
        status = 'done';
      }

      const children =
        status === 'current' && node.id !== currentNodeId
          ? resolveLevel(node.children)
          : forceStatus(node.children, status === 'done' ? 'done' : 'upcoming');

      return { id: node.id, name: node.data.name, status, asIsDate: null, toBeDate: null, children };
    });
  };

  return resolveLevel(nodes);
}

function simulateDates(resolved: ResolvedTrackingLaneNode[], originalById: Map<string, TrackingLaneNode>): void {
  const reachedNodes: ResolvedTrackingLaneNode[] = [];
  const collectReached = (list: ResolvedTrackingLaneNode[]): void => {
    for (const node of list) {
      if (node.status === 'done' || node.status === 'current') reachedNodes.push(node);
      collectReached(node.children);
    }
  };
  collectReached(resolved);

  const today = new Date();
  reachedNodes.forEach((node, index) => {
    const daysAgo = reachedNodes.length - index;
    node.asIsDate = new Date(today.getTime() - daysAgo * DAY_MS);
  });

  let plannedOffsetDays = 2;
  const assignPlanned = (list: ResolvedTrackingLaneNode[]): void => {
    for (const node of list) {
      if (originalById.get(node.id)?.data.variable_to_be) {
        node.toBeDate = new Date(today.getTime() + plannedOffsetDays * DAY_MS);
        plannedOffsetDays += 2;
      }
      assignPlanned(node.children);
    }
  };
  assignPlanned(resolved);
}

function forceStatus(nodes: TrackingLaneNode[], status: TrackingLaneNodeStatus): ResolvedTrackingLaneNode[] {
  return nodes.map(node => ({
    id: node.id,
    name: node.data.name,
    status,
    asIsDate: null,
    toBeDate: null,
    children: forceStatus(node.children, status)
  }));
}

function findAncestorPath(nodes: TrackingLaneNode[], targetId: string): Set<string> | null {
  for (const node of nodes) {
    if (node.id === targetId) return new Set([node.id]);
    const childPath = findAncestorPath(node.children, targetId);
    if (childPath) {
      childPath.add(node.id);
      return childPath;
    }
  }
  return null;
}

function indexById(nodes: TrackingLaneNode[]): Map<string, TrackingLaneNode> {
  const map = new Map<string, TrackingLaneNode>();
  const visit = (list: TrackingLaneNode[]): void => {
    for (const node of list) {
      map.set(node.id, node);
      visit(node.children);
    }
  };
  visit(nodes);
  return map;
}
